import { type User } from 'firebase/auth'
import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'

type AuthState = {
  user: User | null
  pending: boolean
  changing: boolean
  error: Error | null
  setUser: (user: User | null) => void
  setPending: (pending: boolean) => void
  setChanging: (changing: boolean) => void
  setError: (error: Error | null) => void
}

export const useAuthStore = create<AuthState>()(
  subscribeWithSelector(set => ({
    user: null,
    pending: true,
    changing: true,
    error: null,
    setUser: (user: User | null): void => set({ user }),
    setPending: (pending: boolean): void => set({ pending }),
    setChanging: (changing: boolean): void => set({ changing }),
    setError: (error: Error | null): void => set({ error }),
  }))
)

// sync call
export const getCurrentUser = (): User | null => {
  return useAuthStore.getState().user
}

// react call
export const useCurrentUser = (): User | null => {
  return useAuthStore(s => s.user)
}

// Resolves once Firebase auth has finished its initial bootstrap.
// Subsequent calls return immediately; the promise only awaits the
// first transition of `pending` to false.
export const waitForAuthReady = (): Promise<void> => {
  if (!useAuthStore.getState().pending) return Promise.resolve()
  return new Promise(resolve => {
    const unsubscribe = useAuthStore.subscribe(
      s => s.pending,
      pending => {
        if (!pending) {
          unsubscribe()
          resolve()
        }
      }
    )
  })
}

export type UseAuthStateResult = {
  loading: boolean
  error: Error | null
}

export const useAuthState = (): UseAuthStateResult => {
  const loading = useAuthStore(s => s.pending || s.changing)
  const error = useAuthStore(s => s.error)

  return {
    loading,
    error,
  }
}
